import { bitcoinjs } from '@optimex-xyz/btc-script'
import {
  BtcAddressFormat,
  ensureHexPrefix,
  getAddressFormatFromAddress,
  getBtcFeeRate,
  getConfirmedUtxos,
  selectUtxos,
} from '@optimex-xyz/trade-wallet-sdk'

import { NATIVE_SEGWIT_DERIVATION_PATH, TAP_ROOT_DERIVATION_PATH } from '@/config'
import { API_ENDPOINTS, OptimexRpcClient, SUPPORTED_NETWORK } from '@/utils'
import { TransactionOptions, UnisatWalletOptions } from '@/wallets'

import axios from 'axios'
import { toXOnly } from 'bitcoinjs-lib/src/psbt/bip371'
import { ethers } from 'ethers'

import { BaseTransactionOptions, TransactionHelper, TransactionTracker, withRetry } from './transactionHelper'

interface UTXOResponse {
  result?: {
    txid: string
    [key: string]: any
  }[]
}

interface MempoolUTXO {
  txid: string
  vout: number
  status: {
    confirmed: boolean
    block_height: number
    block_hash: string
    block_time: number
  }
  value: number
}

interface BitcoinTransactionOptions extends BaseTransactionOptions {
  networkId: string
  toAddress: string
  amount: number | bigint | string
  sendBitcoinFn: (toAddress: string, amount: any, options?: any) => Promise<string>
  options?: UnisatWalletOptions | TransactionOptions
}

class BitcoinTransactionTracker implements TransactionTracker {
  constructor(
    private readonly networkId: string,
    private readonly toAddress: string
  ) {}

  async trackTransaction(controller: AbortController): Promise<string | null> {
    return withRetry(async () => this.raceForTxid(), { controller })
  }

  private async raceForTxid(): Promise<string | null> {
    try {
      const promises = [
        this.getUTXOsFromRPC().then(this.extractTxid),
        this.getUTXOsFromAPI('mempool').then(this.extractTxid),
        this.getUTXOsFromAPI('blockstream').then(this.extractTxid),
      ]

      const timeout = new Promise<string | null>((resolve) => setTimeout(() => resolve(null), 5000))

      const results = await Promise.allSettled([...promises, timeout])
      for (const result of results) {
        if (result.status === 'fulfilled' && result.value) {
          return result.value
        }
      }
    } catch (error) {
      console.error('Error in raceForTxid:', error)
    }
    return null
  }

  private extractTxid = (data: UTXOResponse | MempoolUTXO[] | null): string | null => {
    if (!data) return null

    if ('result' in data && data.result && data.result.length > 0) {
      return data.result[0].txid
    }

    if (Array.isArray(data) && data.length > 0) {
      return data[0].txid
    }

    return null
  }

  private async getUTXOsFromRPC(): Promise<UTXOResponse | null> {
    try {
      return await new OptimexRpcClient().callRpc<UTXOResponse>(
        this.networkId,
        JSON.stringify({
          method: 'bb_getUTXOs',
          params: [this.toAddress],
        })
      )
    } catch (error) {
      console.error('Error getting UTXOs from RPC:', error)
      return null
    }
  }

  private async getUTXOsFromAPI(provider: 'mempool' | 'blockstream'): Promise<MempoolUTXO[] | null> {
    try {
      const network = this.networkId === SUPPORTED_NETWORK.BTC ? 'mainnet' : 'testnet'
      const baseUrl = API_ENDPOINTS[provider][network]

      const response = await axios.get<MempoolUTXO[]>(`${baseUrl}/address/${this.toAddress}/utxo`)
      return response.data
    } catch (error) {
      console.error(`Error getting UTXOs from ${provider}:`, error)
      return null
    }
  }
}

class BitcoinTransactionHelper extends TransactionHelper<BitcoinTransactionOptions> {
  protected createTracker(options: BitcoinTransactionOptions): TransactionTracker {
    return new BitcoinTransactionTracker(options.networkId, options.toAddress)
  }

  protected sendTransaction(options: BitcoinTransactionOptions): Promise<string> {
    return options.sendBitcoinFn(options.toAddress, options.amount, options.options)
  }

  protected getNetworkId(): string {
    return this.options?.networkId || ''
  }
}

export async function handleBitcoinTransaction(options: BitcoinTransactionOptions): Promise<string> {
  const helper = new BitcoinTransactionHelper(options)
  return helper.handleTransaction(options)
}

export async function generatePsbtSendBtc({
  toAddress,
  _amount,
  fromAddress,
  networkId,
  publicKey,
  options,
}: {
  publicKey: string
  fromAddress: string
  toAddress: string
  _amount: number | bigint // in shatoshi
  networkId: string
  options?: TransactionOptions
}) {
  const amount = Number(_amount)

  const addressType = getAddressFormatFromAddress(fromAddress)
  const isTaproot = addressType === BtcAddressFormat.P2TR
  const isNativeSegwit = addressType === BtcAddressFormat.P2WPKH

  if (!isNativeSegwit && !isTaproot) {
    throw new Error('Unsupported address format. Only native segwit and taproot addresses are supported.')
  }

  const feeRate = options?.feeRate ?? (await getBtcFeeRate(networkId))
  const confirmedUtxos = await getConfirmedUtxos(networkId, fromAddress)

  const { selectedUtxos, totalValue, estimatedFee } = await selectUtxos(
    confirmedUtxos,
    isTaproot ? TAP_ROOT_DERIVATION_PATH : NATIVE_SEGWIT_DERIVATION_PATH,
    fromAddress,
    toAddress,
    amount,
    feeRate,
    options?.feeRateMultiplier
  )

  if (selectedUtxos.length === 0) {
    throw new Error('Insufficient funds for this transaction')
  }

  // Calculate change amount - make sure all values are numbers
  const changeAmount = totalValue - amount - estimatedFee
  const btcNetwork = networkId === SUPPORTED_NETWORK.BTC ? bitcoinjs.networks.bitcoin : bitcoinjs.networks.testnet
  const psbt = new bitcoinjs.Psbt({ network: btcNetwork })

  // Add inputs based on wallet type
  for (const utxo of selectedUtxos) {
    const inputData: any = {
      hash: utxo.txid,
      index: utxo.vout,
      witnessUtxo: {
        script: bitcoinjs.address.toOutputScript(fromAddress, btcNetwork),
        value: BigInt(utxo.value),
      },
    }

    if (isTaproot) {
      // For Taproot (P2TR)
      inputData.tapInternalKey = toXOnly(ethers.getBytes(ensureHexPrefix(publicKey)))
    } else if (isNativeSegwit) {
      // For Native Segwit (P2WPKH)
      // We need the public key hash for p2wpkh
      const pubKeyBuffer = Buffer.from(publicKey.replace(/^0x/, ''), 'hex')
      const pubKeyHash = bitcoinjs.crypto.hash160(pubKeyBuffer)

      inputData.witnessUtxo = {
        script: Buffer.from('0014' + Buffer.from(pubKeyHash).toString('hex'), 'hex'),
        value: BigInt(utxo.value),
      }
    }

    psbt.addInput(inputData)
  }

  // Add recipient output
  psbt.addOutput({
    address: toAddress,
    value: BigInt(amount),
  })

  // Add change output if needed (when amount is greater than dust value)
  if (changeAmount > 546) {
    psbt.addOutput({
      address: fromAddress,
      value: BigInt(Math.floor(changeAmount)), // Ensure integer
    })
  }

  const inputsToSign = selectedUtxos.map((_, index) => ({
    address: fromAddress,
    index,
  }))

  return { inputsToSign, psbt, btcNetwork, selectedUtxos }
}

export async function getBtcConfirmationCount(txid: string, network: string) {
  try {
    const apiUrl: string = API_ENDPOINTS.mempool[network === SUPPORTED_NETWORK.BTC ? 'mainnet' : 'testnet']

    const responses = await Promise.all([axios.get(`${apiUrl}/tx/${txid}`), axios.get(`${apiUrl}/blocks/tip/height`)])
    const formattedTxResponse = responses.map((e) => e.data)

    const txData = formattedTxResponse[0]
    const currentBlockHeight = parseInt(formattedTxResponse[1])

    // Calculate confirmations
    if (txData?.status?.confirmed) {
      // If confirmed, confirmations = current block height - transaction block height + 1
      const confirmations = txData.status.block_height ? currentBlockHeight - txData.status.block_height + 1 : 0
      return confirmations
    } else {
      return 0
    }
  } catch {
    return 0
  }
}
